// QR Kiosk v2 — KV2-6 token-styled markdown wrapper for bot bodies. The
// `preprocessMarkdown` helper (+ its three predicates) is COPIED VERBATIM from
// shared/chatBot/components/ChatBotMessage.tsx:50–116 — copy-not-import, the KV2
// precedent (kioskLiveStatus.ts): the source file is MUI-poisoned so it must not
// enter the v2 module graph. Rendering is react-markdown + remark-gfm with
// `skipHtml` (no dangerouslySetInnerHTML → no HTML-injection path, §8). Overrides
// use inline styles + scoped tokens only.
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { renderKioskText } from "./KioskCite";

/** True if the line is a bold-only header like "**DENNÁ (8 hod)**" */
const isBoldHeaderLine = (line: string): boolean => /^\*\*.+\*\*$/.test(line.trim());

/** True if the line is already a markdown list item (- item, * item, 1. item) */
const isAlreadyListItem = (line: string): boolean => /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);

/** True if the line is a markdown heading (# heading) */
const isMarkdownHeading = (line: string): boolean => /^#{1,6}\s+/.test(line.trim());

/** §8.5 REGRESSION FIX (Round-6): true if the line is a GFM table row / separator
 *  (starts with `|`). Without this, a table that follows a `**bold header**` had every
 *  row `- `-prefixed into a list → remark-gfm never saw a table → raw-pipe mess. */
const isTableLine = (line: string): boolean => /^\s*\|/.test(line.trim());

/** Pre-process bot text so it renders correctly in ReactMarkdown (verbatim copy). */
const preprocessMarkdown = (text: string): string => {
  if (!text) return "";
  let processed = text.split("\\r\\n").join("\n").split("\\n").join("\n");
  processed = processed.replace(/^[•●◦▪▸►]\s*/gm, "- ");
  const lines = processed.split("\n");
  const result: string[] = [];
  let afterBoldHeader = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (isBoldHeaderLine(trimmed)) {
      afterBoldHeader = true;
      result.push(line);
    } else if (afterBoldHeader && trimmed.length > 0) {
      if (isAlreadyListItem(trimmed) || isMarkdownHeading(trimmed) || isTableLine(trimmed)) {
        result.push(line);
      } else {
        result.push(`- ${trimmed}`);
      }
    } else {
      if (trimmed.length === 0) afterBoldHeader = false;
      result.push(line);
    }
  }
  return result.join("\n");
};

// §6c kiosk-legibility recalibration (base 15px): body 13.5 → 15, heading 14 → 16,
// inline code 12 → 13, block code 12 → 12.5.
const pStyle: React.CSSProperties = { margin: "0 0 8px", fontSize: 15, lineHeight: 1.6, color: "var(--ink)" };
const listStyle: React.CSSProperties = { margin: "0 0 8px", paddingLeft: 20, fontSize: 15, lineHeight: 1.6, color: "var(--ink)" };
const headingStyle: React.CSSProperties = { margin: "8px 0 6px", fontSize: 16, fontWeight: 700, color: "var(--ink)" };
const codeInlineStyle: React.CSSProperties = {
  // §6b: force inline so an inline code can never be painted as a full-width block,
  // even nested in a stacked table card; whiteSpace normal so long codes may wrap.
  display: "inline",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  padding: "1px 5px",
  background: "var(--accent-soft)",
  color: "var(--accent)",
  border: "1px solid var(--accent-line)",
  borderRadius: 4,
  whiteSpace: "normal",
};
const codeBlockStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-mono)",
  fontSize: 12.5,
  lineHeight: 1.5,
  padding: "10px 12px",
  margin: "0 0 8px",
  background: "var(--bg-soft)",
  color: "var(--ink-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  overflowX: "auto",
  whiteSpace: "pre",
};

const components: any = {
  // §5a: renderKioskText substitutes ⟦KREF…⟧ citation sentinels inline with tap-
  // able chips in the text-bearing renderers (p / strong / li / th / td).
  p: ({ children }: any) => <p style={pStyle}>{renderKioskText(children)}</p>,
  strong: ({ children }: any) => <strong style={{ fontWeight: 700 }}>{renderKioskText(children)}</strong>,
  // Italic is BANNED kiosk-wide (§1b.1 global rule — poor readability). Markdown
  // emphasis keeps its semantic tag but renders upright with weight instead.
  em: ({ children }: any) => <em style={{ fontStyle: "normal", fontWeight: 600 }}>{children}</em>,
  ul: ({ children }: any) => <ul style={listStyle}>{children}</ul>,
  ol: ({ children }: any) => <ol style={listStyle}>{children}</ol>,
  li: ({ children }: any) => <li style={{ marginBottom: 3 }}>{renderKioskText(children)}</li>,
  h1: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  h2: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  h3: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  h4: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  h5: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  h6: ({ children }: any) => <div style={headingStyle}>{children}</div>,
  a: ({ children, href }: any) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--tier-accent)", textDecoration: "underline" }}>
      {children}
    </a>
  ),
  // §6a responsive tables — classed semantic elements; phone stacking + desktop
  // scroll are CSS-driven (kiosk-v2-tokens.css §1.5), class-switched by MidasBotBody.
  table: ({ children }: any) => (
    <div className="kv2-md-tblwrap">
      <table className="kv2-md-tbl">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead>{children}</thead>,
  th: ({ children }: any) => <th>{renderKioskText(children)}</th>,
  td: ({ children }: any) => <td>{renderKioskText(children)}</td>,
  // §6b: react-markdown v9 REMOVED the `inline` prop, so the `code` renderer is
  // called for BOTH inline and fenced code with inline === undefined. Relying on
  // `if (inline)` sent EVERY inline code down the block branch (full-width grey
  // boxes inside table cells / prose). Detect inline-vs-block from the real v9
  // signals; honour `inline` if a future react-markdown restores it.
  code: ({ inline, children, className }: any) => {
    const codeText =
      typeof children === "string"
        ? children
        : Array.isArray(children) && children.length === 1 && typeof children[0] === "string"
        ? children[0]
        : null;
    const hasLanguageClass = /language-/.test(className || "");
    const isMultiline = codeText != null && codeText.includes("\n");
    const isInline = inline === true || (inline === undefined && !hasLanguageClass && !isMultiline);
    if (isInline) {
      // §7.2.1 (Round-6): never paint an empty inline-code husk (the little empty rounded
      // boxes in the owner's screenshot — a stripped no-surface tag left `` ` ` ``).
      const isEmptyInline =
        children == null ||
        children === "" ||
        (Array.isArray(children) && children.length === 0) ||
        (typeof codeText === "string" && codeText.trim() === "");
      if (isEmptyInline) return null;
      return <code style={codeInlineStyle}>{children}</code>;
    }
    return <code style={codeBlockStyle}>{children}</code>;
  },
  pre: ({ children }: any) => <>{children}</>,
};

export function MidasMarkdown({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
      {preprocessMarkdown(text)}
    </ReactMarkdown>
  );
}

export default MidasMarkdown;
